import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, User, Pencil, Trash2, FileCheck } from "lucide-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AxiosError } from "axios";
import { listForms, createForm, deleteForm } from "@/features/forms";
import type { FormListItem } from "@/features/forms";
import { useTaxYearStore } from "@/features/tax-year/store/taxYearStore";
import { handleApiError } from "@/lib/utils/errorHandler";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FORM_FILING_TYPE_API = { INDIVIDUAL: "INDIVIDUAL", BUSINESS: "BUSINESS" } as const;

export default function MyFormsPage() {
  const navigate = useNavigate();
  const taxYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState<"INDIVIDUAL" | "BUSINESS" | null>(null);
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);
  const [businessFormName, setBusinessFormName] = useState("");

  const fetchForms = useCallback(async () => {
    if (taxYear == null) return;
    setLoading(true);
    try {
      const list = await listForms({ taxYear });
      setForms(list);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => {
    if (!configFetched || taxYear == null) return;
    fetchForms();
  }, [configFetched, taxYear, fetchForms]);

  const individualForm = forms.find((f) => f.filingType === FORM_FILING_TYPE_API.INDIVIDUAL);
  const businessForms = forms.filter((f) => f.filingType === FORM_FILING_TYPE_API.BUSINESS);
  const hasIndividual = !!individualForm;

  const handleAddIndividual = async () => {
    if (hasIndividual || taxYear == null) return;
    setAdding("INDIVIDUAL");
    try {
      const created = await createForm({ filingType: "INDIVIDUAL", taxYear });
      const id = (created as { id?: string }).id ?? (created as { _id?: string })._id;
      if (id) {
        toast.success("Individual form created");
        navigate(`/file-tax/edit/${id}`);
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 400) {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Individual form already exists for this tax year.";
        toast.error(msg);
      } else {
        handleApiError(err);
      }
    } finally {
      setAdding(null);
    }
  };

  const handleOpenAddBusiness = () => {
    setBusinessFormName("");
    setAddBusinessOpen(true);
  };

  const handleAddBusinessSubmit = async () => {
    if (taxYear == null) return;
    setAdding("BUSINESS");
    setAddBusinessOpen(false);
    const formName = businessFormName.trim() || undefined;
    try {
      const created = await createForm({
        filingType: "BUSINESS",
        taxYear,
        ...(formName && { formName }),
      });
      const id = (created as { id?: string }).id ?? (created as { _id?: string })._id;
      if (id) {
        toast.success("Business form created");
        navigate(`/file-tax/edit/${id}`);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setAdding(null);
    }
  };

  const handleEdit = (id: string) => {
    navigate(`/file-tax/edit/${id}`);
  };

  const handleDeleteClick = (id: string) => setDeleteId(id);
  const handleDeleteCancel = () => setDeleteId(null);

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteForm(deleteId);
      toast.success("Form deleted");
      setDeleteId(null);
      await fetchForms();
    } catch (err) {
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      if (status === 404 || status === 403) {
        setDeleteId(null);
        await fetchForms();
        toast.error(
          status === 404
            ? "Form not found. It may have been already deleted."
            : "You don't have permission to delete this form."
        );
      } else {
        handleApiError(err);
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 sm:py-8">
        <div className="mx-auto max-w-3xl space-y-6">

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                My tax forms
              </h1>
              <p className="text-muted-foreground mt-1">
                View, add, or edit your tax forms{taxYear != null ? ` for ${taxYear}` : ""}.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddIndividual}
                disabled={hasIndividual || adding !== null || taxYear == null}
              >
                <User className="h-4 w-4 mr-2" />
                Add individual form
              </Button>
              <Button
                size="sm"
                onClick={handleOpenAddBusiness}
                disabled={adding !== null || taxYear == null}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Add business form
              </Button>
            </div>
          </div>

          {loading || !configFetched ? (
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ) : forms.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>No forms yet</CardTitle>
                <CardDescription>
                  Add an individual or business form to get started.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleAddIndividual}
                    disabled={adding !== null}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Add individual form
                  </Button>
                  <Button onClick={handleOpenAddBusiness} disabled={adding !== null}>
                    <Building2 className="h-4 w-4 mr-2" />
                    Add business form
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Your forms
                </CardTitle>
                <CardDescription>
                  Individual: one per year. Business: multiple allowed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {individualForm && (
                    <li className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">Individual</span>
                        <span className="text-muted-foreground text-sm">
                          Tax year {individualForm.taxYear ?? taxYear}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(individualForm.id)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteClick(individualForm.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </li>
                  )}
                  {businessForms.map((form, idx) => (
                    <li
                      key={form.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-3"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {form.formName?.trim() || `Business Form ${idx + 1}`}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          Tax year {form.taxYear ?? taxYear}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(form.id)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteClick(form.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {adding && (
            <p className="text-sm text-muted-foreground">
              Creating {adding === "INDIVIDUAL" ? "individual" : "business"} form…
            </p>
          )}
        </div>
      </div>

      <Dialog open={addBusinessOpen} onOpenChange={(open) => !open && setAddBusinessOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add business form</DialogTitle>
            <DialogDescription>
              Optionally give this form a name so you can tell it apart from other business forms.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="business-form-name">Business form name</Label>
            <Input
              id="business-form-name"
              placeholder="e.g. Main LLC, Side Gig"
              value={businessFormName}
              onChange={(e) => setBusinessFormName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddBusinessSubmit()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBusinessOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddBusinessSubmit}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && handleDeleteCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete form?</DialogTitle>
            <DialogDescription>
              This cannot be undone. The form and its data will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
